import React from 'react';
import PropTypes from 'prop-types';
import './review.css';

/**
 * The recorded approval DECISIONS for one item — who decided what, why, and against
 * which revision.
 *
 * WP1 (#590/#603/#592/#589) made the backend capture all of this and return it on
 * `GET /items/{id}/breakdown`; nothing rendered it, so the Breakdown tab showed a single
 * pill and silently dropped every field an auditor actually needs. This component is the
 * display half:
 *
 * - **F-06 / REQ-6096 (#603, D-09)** — the mandatory rejection `reason`. Capturing it and
 *   not showing it would leave the author with the same unactionable "not approved" the
 *   requirement exists to prevent.
 * - **F-08 / REQ-6098 (#603, D-08)** — the `justification` given when an approval
 *   superseded a live objection.
 * - **F-09 / REQ-6099 (#603, D-08)** — BOTH decisions stay visible, with both parties
 *   named (`superseded_reviewers`). The per-reviewer pill above can never show this: it
 *   renders the caller's OWN decision (F-05 / REQ-6095, #598), and `item_approvals` is
 *   keyed `(item_uuid, reviewer)`, so one reviewer's row can never be both.
 * - **F-12 / REQ-6102 (#592, D-05)** — `approved_at_version` + `version_drift`, so a
 *   decision that reads fresh only because a restore brought the signed-off content back
 *   shows as "Approved at v3 · restored at v7" rather than a bare "Approved".
 * - **F-52 / REQ-6113 (#591, D-04)** — `self_approved`. Self-approval is permitted, so
 *   this is a visible audit marker, never a gate.
 */

const STATE_META = {
  approved: { glyph: '✓', cls: 'ok', label: 'Approved' },
  not_approved: { glyph: '✕', cls: 'no', label: 'Not approved' },
};

/**
 * `superseded_reviewers` (`item_approvals`/`item_approval_events`) is stored as a
 * JSON-encoded TEXT column, but every read surface (`/breakdown`, the History feed,
 * the `PUT /approval` response) now decodes it server-side —
 * `core.requirements.feedback._decode_superseded_reviewers`, run inside the shared
 * `annotate_approvals`/`annotate_approval_events` annotators (#623) — so it always
 * arrives here as a parsed list, or `null`/absent when empty. This is a plain array
 * read, not string-tolerant: if a payload ever regresses to a raw JSON string, this
 * is meant to fail loudly (empty list rendered) rather than silently re-parse it.
 */
export function parseSuperseded(value) {
  return Array.isArray(value) ? value : [];
}

function who(row) {
  return row.actor || row.reviewer || 'unknown reviewer';
}

function ApprovalDecision({ row, currentVersion, onOverride }) {
  const meta = STATE_META[row.state];
  if (!meta) return null;
  const superseded = parseSuperseded(row.superseded_reviewers);
  // F-12 / REQ-6102: `version_drift` is set by the backend only when the decision is
  // FRESH yet the item has moved past the revision it was issued against — i.e. the
  // content came back via a restore or an archive/unarchive cycle. A stale decision is
  // not drift, it is simply out of date, and says so via `stale`.
  const drift = !row.stale && row.version_drift && row.approved_at_version != null;

  return (
    <li
      className={`rmdv-decision ${meta.cls}${row.stale ? ' stale' : ''}`}
      data-testid={`approval-decision-${row.reviewer}`}
    >
      <span className="rmdv-decision-head">
        <span className="rmdv-decision-glyph" aria-hidden="true">{meta.glyph}</span>
        <span className="rmdv-decision-state">{meta.label}</span>
        <span className="rmdv-decision-who">{who(row)}</span>

        {/* F-52 / REQ-6113 (#591, D-04): a sign-off the author gave themselves. Permitted,
            but it must be identifiable at a glance — an auditor should never have to join
            the approval back to the item's author to discover it. */}
        {row.self_approved && (
          <span
            className="badge warning"
            data-testid={`self-approved-${row.reviewer}`}
            title="This reviewer is the item's author — a self-issued sign-off"
          >
            self-approved
          </span>
        )}

        {/* F-11 / REQ-6101 (#603): rejections go stale symmetrically with approvals, so
            this marker is deliberately worded for either decision. */}
        {row.stale && (
          <span
            className="badge neutral"
            data-testid={`stale-decision-${row.reviewer}`}
            title="The item changed after this decision was recorded — it no longer applies to the current content"
          >
            stale
          </span>
        )}

        {/* F-12 / REQ-6102 (#592, D-05). */}
        {drift && (
          <span
            className="badge neutral"
            data-testid={`version-drift-${row.reviewer}`}
            title="This decision was issued against an earlier revision; the signed-off content was later restored, so it still applies"
          >
            {`at v${row.approved_at_version}${currentVersion != null ? ` · restored at v${currentVersion}` : ''}`}
          </span>
        )}

        {/* F-09 / REQ-6099 (#603, D-08): name the party overruled — "both parties are
            retained" is unverifiable if only the winner is shown. */}
        {superseded.length > 0 && (
          <span
            className="badge accent"
            data-testid={`superseded-${row.reviewer}`}
            title="This approval superseded a live rejection; the rejection is retained on the record"
          >
            {`over ${superseded.join(', ')}`}
          </span>
        )}

        {/* F-61 / REQ-6119 (#606, D-10, #629): the decision STANDS — this is an
            annotation, never a gate, so it sits beside the state, not instead of it.
            Wording is the decision's own — "user inactive", never "left org" — and is
            plain TEXT (never colour-only, F-23 / REQ-6109). */}
        {row.reviewer_inactive && (
          <span
            className="badge neutral"
            data-testid={`reviewer-inactive-${row.reviewer}`}
            title="This reviewer no longer has access — the decision still stands and counts"
          >
            user inactive
          </span>
        )}

        {/* F-60 / REQ-6118 (#606, #629): the admin-override entry point — clears
            ANOTHER reviewer's recorded decision. Always rendered; the backend is the
            control (403 without approval authority, 400 without a reason via the
            confirm dialog below), matching how ApprovePill's own writes are gated. */}
        {onOverride && (
          <button
            type="button"
            className="linkbtn"
            data-testid={`override-decision-${row.reviewer}`}
            onClick={() => onOverride(row.reviewer)}
          >
            Override
          </button>
        )}
      </span>

      {/* F-06 / REQ-6096 (#603, D-09). Rendered as text (never dangerouslySetInnerHTML) —
          reviewer-supplied free text, same treatment comment bodies already get. */}
      {row.reason && (
        <span className="rmdv-decision-text" data-testid={`decision-reason-${row.reviewer}`}>
          <span className="rmdv-decision-text-label">Reason:</span> {row.reason}
        </span>
      )}

      {/* F-08 / REQ-6098 (#603, D-08). */}
      {row.justification && (
        <span
          className="rmdv-decision-text"
          data-testid={`decision-justification-${row.reviewer}`}
        >
          <span className="rmdv-decision-text-label">Justification:</span> {row.justification}
        </span>
      )}
    </li>
  );
}

ApprovalDecision.propTypes = {
  row: PropTypes.object.isRequired,
  currentVersion: PropTypes.number,
  onOverride: PropTypes.func,
};

function ApprovalDecisions({ rows, currentVersion = null, itemId, onOverride = null }) {
  const decided = (rows || []).filter(
    (r) => r && (r.state === 'approved' || r.state === 'not_approved'),
  );
  if (decided.length === 0) return null;
  return (
    <ul className="rmdv-decisions" data-testid={`approval-decisions-${itemId}`}>
      {decided.map((row) => (
        <ApprovalDecision
          key={row.reviewer}
          row={row}
          currentVersion={currentVersion}
          onOverride={onOverride ? () => onOverride(row.reviewer) : null}
        />
      ))}
    </ul>
  );
}

ApprovalDecisions.propTypes = {
  rows: PropTypes.array,
  currentVersion: PropTypes.number,
  itemId: PropTypes.string.isRequired,
  // F-60 / REQ-6118 (#606, #629): (reviewer) => void — opens the admin-override
  // confirm dialog for that reviewer's decision. Omitted entirely hides the control
  // (used by any read-only consumer of this component, if one is ever added).
  onOverride: PropTypes.func,
};

export default ApprovalDecisions;
